<template>
  <VBackdrop v-if="loaded" @click="props.closeViaBackdrop && emit('close')">
    <component :is="dialogWrapper" v-bind="dialogWrapperProps">
      <div
        role="dialog"
        aria-modal="true"
        class="modern-color-theme font-poppins p-5 bg-white rounded-2xl gap-6 flex flex-col shadow-modal"
        :class="{ 'relative': props.closeViaButton }"
        :style="computedLayout"
        v-bind="$attrs"
        data-component-name="VDialog"
        @click.stop
      >
        <VButton v-if="props.closeViaButton" variant="text" color="secondary" icon :container-props="{ class: 'absolute top-2 right-2' }" @click="emit('close')">
          <VIcon :name="props.closeViaButtonIcon" />
        </VButton>
        <div v-if="props.icon || slots.title || props.title" class="flex flex-col items-center text-center gap-4">
          <VIcon v-if="props.icon" color="neutral/650" :name="props.icon" />
          <slot name="title">
            <VHeading v-if="props.title" v-a11y:dialog:title color="neutral/600" level="5" medium>
              {{ props.title }}
            </VHeading>
          </slot>
        </div>
        <div class="flex flex-col gap-4 h-full pr-2 -mr-2 overflow-y-auto">
          <slot />
        </div>
        <div v-if="slots.buttons" class="flex justify-end gap-4">
          <slot name="buttons" />
        </div>
      </div>
    </component>
  </VBackdrop>
</template>
<script setup lang="ts">
import VButton from '@component-library/buttons/VButton.vue';
import VBackdrop from '../layouts/VBackdrop.vue'
import VIcon from '@component-library/labels/VIcon.vue';
import VZone from '@component-library/utilities/VZone.vue';
import { computed, onBeforeMount, onMounted, ref, type Component } from 'vue';
import { useLoader } from '@component-utils/loader';
import { useErrorToast } from '@component-utils/toasts';
import type { Icon } from '@icons/index';
import type { V } from '@component-utils/types';
import VHeading from '@component-library/labels/VHeading.vue';

defineOptions({
  name: 'VDialog',
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<{
    /**
     * Width, defaults to 500px
     */
    width?: string
    /**
     * Height
     */
    height?: string
    /**
     * Maximum width, defaults to width
     */
    maxWidth?: string
    /**
     * Maximum height, defaults to height or 95vh when height is not supplied
     */
    maxHeight?: string
    /**
     * Allows closing of dialog via X button in top right corner. Requires close emit to be handled by parent component.
     */
    closeViaButton?: boolean
    /**
     * Use different icon when closing the dialog
     */
    closeViaButtonIcon?: Icon
    /**
     * Allows closing of dialog via click on the backdrop. Requires close emit to be handled by parent component.
     */
    closeViaBackdrop?: boolean
    /**
     * Allows data to be loaded before dialog is displayed
     */
    load?: () => Promise<void>
    /**
     * Icon to show during loading
     */
    loadIcon?: Icon
    /**
     * Message to show during loading
     */
    loadMessage?: string
    /**
     * Dialog title. If title cannot be handled via this approach please the #title slot. Please ensure you are using `v-a11y:dialog:title` directive to label the title as a title of the dialog if using a slot.
     */
    title?: string
    /**
     * Icon to display above the dialog title
     */
    icon?: Icon
    /**
     * Optional zone name for semantic structure identification
     */
    zone?: V.Zone
  }>(),
  {
    width: '500px',
    height: undefined,
    closeViaBackdrop: false,
    closeViaButton: false,
    closeViaButtonIcon: 'Solid/close',
    load: undefined,
    loadIcon: undefined,
    loadMessage: undefined,
    title: undefined,
    icon: undefined,
    maxWidth: undefined,
    maxHeight: undefined,
    zone: undefined
  }
)

const emit = defineEmits<{
  close: []
}>()

const slots = defineSlots<{
  title?(): unknown
  default(): unknown
  buttons?(): unknown
}>()

const computedLayout = computed(() => {
  const w = props.width
  const wMax = props.maxWidth ?? props.width ?? '95vw'

  const h = props.height
  const hMax = props.maxHeight ?? props.height ?? '95vh'

  return {
    maxWidth: wMax,
    width: w ? `min(${w}, ${wMax})` : undefined,
    minWidth: w,
    maxHeight: hMax,
    height: h ? `min(${h}, ${hMax})` : undefined,
    minHeight: h
  }
})

const dialogWrapper = computed(() => {
  if (props.zone) {
    return VZone as Component
  }
  return 'div'
})
const dialogWrapperProps = computed(() => props.zone ? { name: props.zone } : {})

const loaded = ref(!props.load)

onBeforeMount(() => {
  // Click on the body so all dropdowns and context menus are closed as we cannot guarantee no z-index issues with current codebase
  document.body.click()
})

onMounted(async () => {
  if (props.load) {
    const loader = useLoader()
    try {
      loader.start({ opaque: false, message: props.loadMessage, icon: props.loadIcon })

      await props.load()

      loaded.value = true
    } catch (e) {
      useErrorToast(e)

      emit('close')
    } finally {
      loader.stop()
    }
  }
})
</script>
